import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Machine } from './ts/Connections';
// Use SpectreResponse type for response
import type { FeatureFlagsResponse } from './ts/SpectreResponse';

/**
 * Interface defining all available feature flags from Spectre Core.
 * All flags default to false for safety - features are opt-in based on machine support.
 * 
 * Add new feature flags here as Spectre Core adds them.
 */
export interface FeatureFlags {
    /** Whether the machine supports the feature flags API at all */
    FeatureFlags: boolean;
    /** Whether the machine supports settings locking (IsLocked API) */
    SettingsLocking: boolean;
    /** Whether the machine supports recall feature */
    Recall: boolean;
    /** Whether the machine supports attaching files to scans */
    AttachFile: boolean;
    /** Whether the machine supports the login API (SetLoginInfo / isLicensed) */
    LoginAPI: boolean;
    // Add new feature flags here as they are added to Spectre Core
}

/** Default feature flags - all disabled for safety */
const defaultFeatureFlags: FeatureFlags = {
    FeatureFlags: false,
    SettingsLocking: false,
    Recall: false,
    AttachFile: false,
    LoginAPI: false,
};

interface FeatureFlagsContextType {
    /** Current feature flags from the connected machine */
    featureFlags: FeatureFlags;
    /** Whether feature flags are still being loaded */
    loading: boolean;
    /** Any error that occurred while fetching feature flags */
    error: Error | null;
    /** Manually refresh feature flags from the machine */
    refreshFeatureFlags: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
    children: ReactNode;
    machine: Machine | null | false;
}

/**
 * Provider component that fetches and provides feature flags from the connected Spectre Core machine.
 * Wrap this around components that need to check feature support.
 * 
 * @example
 * <FeatureFlagsProvider machine={selectedMachine}>
 *   <Control ... />
 * </FeatureFlagsProvider>
 */
export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children, machine }) => {
    const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchFeatureFlags = async () => {
        if (!machine || !machine.connection) {
            setFeatureFlags(defaultFeatureFlags);
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response: FeatureFlagsResponse = await machine.connection.get('featureflags');

            // Check if the response has the expected structure
            if (response?.Responses?.FeatureFlags) {
                const flags = response.Responses.FeatureFlags;
                
                // If code is not "0", there was an error
                if (flags.code && flags.code !== "0") {
                    console.warn('Feature flags returned error code:', flags.code);
                    setFeatureFlags(defaultFeatureFlags);
                } else {
                    // Merge returned flags with defaults (in case machine doesn't return all flags)
                    setFeatureFlags({
                        ...defaultFeatureFlags,
                        FeatureFlags: Boolean(flags.FeatureFlags),
                        SettingsLocking: Boolean(flags.SettingsLocking),
                        Recall: Boolean(flags.Recall),
                        AttachFile: Boolean(flags.AttachFile),
                        LoginAPI: Boolean(flags.LoginAPI),
                        // Add new flags here as they are added
                    });
                }
            } else {
                // Machine doesn't support feature flags API - use defaults
                setFeatureFlags(defaultFeatureFlags);
            }
        } catch (err) {
            // Non-fatal: if feature flags fetch fails, use defaults
            // This handles older machines that don't have the feature flags endpoint
            setError(err instanceof Error ? err : new Error(String(err)));
            setFeatureFlags(defaultFeatureFlags);
        } finally {
            setLoading(false);
        }
    };

    // Fetch feature flags when machine changes
    useEffect(() => {
        fetchFeatureFlags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine]);

    const refreshFeatureFlags = async () => {
        await fetchFeatureFlags();
    };

    return (
        <FeatureFlagsContext.Provider value={{ featureFlags, loading, error, refreshFeatureFlags }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
};

/**
 * Hook to access feature flags from the context.
 * Must be used within a FeatureFlagsProvider.
 * 
 * @example
 * const { featureFlags, loading } = useFeatureFlags();
 * if (featureFlags.SettingsLocking) {
 *   // Call settings locking API
 * }
 */
export const useFeatureFlags = (): FeatureFlagsContextType => {
    const context = useContext(FeatureFlagsContext);
    if (context === undefined) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
    }
    return context;
};

/**
 * Hook to check if a specific feature is supported.
 * Convenience wrapper around useFeatureFlags.
 * 
 * @example
 * const isSettingsLockingSupported = useFeatureSupported('SettingsLocking');
 * const isRecallSupported = useFeatureSupported('Recall');
 */
export const useFeatureSupported = (featureName: keyof FeatureFlags): boolean => {
    const { featureFlags } = useFeatureFlags();
    return featureFlags[featureName] ?? false;
};
